import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Platform } from '@ionic/angular';
import { ConfigService } from 'src/app/services/config/config.service';
import { EMAIL_PATTERN_V2, NAME_PATTERN, birthdayValidator } from 'src/app/services/customFormValidators';
import { Navigator } from 'src/app/components/menu/navigator';
import { EventsService } from 'src/app/services/event/event.service';
import { UserSessionService } from 'src/app/services/user-session/user-session.service';
import { ContestService } from 'src/app/services/contest/contest.service';
import { CustomerModel } from 'src/app/models/customer-model';

type Colors = {
  bgrcolor?: string;
  borderColor?: string;
  descriptionColor?: string;
};

@Component({
  selector: 'app-contest',
  templateUrl: './contest.component.html',
  styleUrls: ['./contest.component.scss'],
})

export class ContestComponent implements OnInit {
  @Input() contestData: any

  private readonly CDN_PATH = `${this.configService?.getConfig()?.CDN_BASEURL}/assets/contests/`;
  protected showForm: boolean = true;
  protected submissionSuccesful: boolean = false;
  public customer: CustomerModel;
  public colors: Colors;
  public legalAge: number;
  protected buttonDisabled: boolean = false;
  public checkboxText: string = this.configService?.getConfig().LABEL_CONTEST_CHECKBOX_TEXT ?? 'I would like to receive special offers about events, promotions and other opportunities of Boyd Gaming.';
  public displayMessage: string;
  public errorDetected: boolean = false;

  public defaultColors: Colors = {
    bgrcolor: 'transparent',
    borderColor: 'white',
    descriptionColor: 'rgba(0, 0, 0, 0.5)'
  };

  public contestForm: FormGroup;
  public dateOfBirthGroup: AbstractControl;
  public dateOfBirthYear: AbstractControl;
  public dateOfBirthDay: AbstractControl;
  public dateOfBirthMonth: AbstractControl;
  public firstName: AbstractControl;
  public lastName: AbstractControl;


  constructor(
    private readonly navigator: Navigator,
    private readonly contestService: ContestService,
    private readonly configService: ConfigService,
    private readonly platform: Platform,
    private readonly event: EventsService,
    private readonly user: UserSessionService
  ) {
    this.buttonDisabled = false;
  }

  ngOnInit() {

    try {
      this.colors = JSON.parse(this.contestData?.formColor) ?? this.defaultColors;
    } catch (error) {
      console.error('Error parsing formColor:', error);
      this.colors = this.defaultColors;
    }

    this.contestService.getValidContest().then((contests) => {
      if (contests.length === 0) {
        this.showForm = false;
      } else {
        this.showForm = true;
        this.legalAge = this.configService.getConfig().FEATURE_LEGAL_AGE ?? 21;

        this.contestForm = new FormGroup({
          firstName: new FormControl({ value: '', disabled: !!this.customer?.firstName }, [Validators.required, Validators.pattern(NAME_PATTERN)]),
          lastName: new FormControl({ value: '', disabled: !!this.customer?.lastName }, [Validators.required, Validators.pattern(NAME_PATTERN)]),
          dateOfBirthGroup: new FormGroup(
            {
              dateOfBirthYear: new FormControl({ value: '', disabled: !!this.customer?.birthDate }, [Validators.required]),
              dateOfBirthDay: new FormControl({ value: '', disabled: !!this.customer?.birthDate }, [Validators.required]),
              dateOfBirthMonth: new FormControl({ value: '', disabled: !!this.customer?.birthDate }, [Validators.required]),
              hiddenLegalAge: new FormControl(this.legalAge)
            },
            birthdayValidator
          ),
          allowPromoEmails: new FormControl(false),
          email: new FormControl({ value: '', disabled: !!this.customer?.email }, [Validators.pattern(EMAIL_PATTERN_V2), Validators.required, Validators.maxLength(250)])
        });

        this.firstName = this.contestForm.get('firstName');
        this.lastName = this.contestForm.get('lastName');

        this.dateOfBirthGroup = this.contestForm.get('dateOfBirthGroup');
        this.dateOfBirthYear = this.dateOfBirthGroup.get('dateOfBirthYear');
        this.dateOfBirthDay = this.dateOfBirthGroup.get('dateOfBirthDay');
        this.dateOfBirthMonth = this.dateOfBirthGroup.get('dateOfBirthMonth');

        this.predefineValue();
      }
    })
      .catch((error) => {
        console.error('Error fetching valid contests', error);
      });
  }

  ionViewWillLeave() {
    this.displayMessage = null;
  }

  predefineValue() {
    this.user.getCustomer().subscribe((customer: any) => {
      this.customer = customer;
      if (customer) {
        if (customer.email) this.contestForm.get("email").setValue(customer.email)
        if (customer.firstName) this.contestForm.get("firstName").setValue(customer.firstName)
        if (customer.lastName) this.contestForm.get("lastName").setValue(customer.lastName)
        if (customer.birthDate) {
          var birthDateFormattedArray = customer.birthDate.split('-');
          var usersYear = Number(birthDateFormattedArray[0]);
          var usersMonth = Number(birthDateFormattedArray[1]);
          var usersDay = birthDateFormattedArray[2]; // don't make it a Number !!!
          this.contestForm.patchValue({
            dateOfBirthGroup: {
              dateOfBirthDay: customer.birthDate ? usersDay : '',
              dateOfBirthMonth: customer.birthDate ? usersMonth : '',
              dateOfBirthYear: customer.birthDate ? usersYear : '',
            }
          })
        }
        if (customer.allowPromoEmails !== undefined) {
          this.contestForm.get("allowPromoEmails").setValue(customer.allowPromoEmails);
        }
      }
    })
  }

  public async doSubmit(): Promise<void> {
    if (this.buttonDisabled) {
      return;
    }

    this.buttonDisabled = true;

    const data = this.contestForm.value;
    const dob = this.formatDateOfBirth(data.dateOfBirthGroup);

    const formObj = {
      firstName: data.firstName,
      birthDate: dob.toISOString().split("T")[0],
      lastName: data.lastName,
      email: data.email,
      allowPromoEmails: data.allowPromoEmails,
      contest_id: this.contestData.id,
    };

    if (this.customer?.allowPromoEmails !== formObj.allowPromoEmails) {
      try {
        await this.contestService.postAllowEmail(formObj.allowPromoEmails).toPromise();
      } catch (updateError) {
        console.error('Error updating customer record', updateError);
      }
    }

    try {
      const res: any = await this.contestService.postContest(formObj).toPromise();
      this.handleSubmissionSuccess();
    } catch (error) {
      this.handleSubmissionError(error);
    } finally {
      setTimeout(() => (this.buttonDisabled = false), 2000);
    }
  }

  private formatDateOfBirth(dateOfBirthGroup): Date {
    const month =
      ~~dateOfBirthGroup.dateOfBirthMonth < 10
        ? "0" + dateOfBirthGroup.dateOfBirthMonth
        : dateOfBirthGroup.dateOfBirthMonth;

    return new Date(
      dateOfBirthGroup.dateOfBirthYear + "-" + month + "-" + dateOfBirthGroup.dateOfBirthDay
    );
  }

  private handleSubmissionSuccess(): void {
    this.errorDetected = false;
    this.event.publish("event:load-contest");
    this.showForm = false;
    this.submissionSuccesful = true;
    this.contestForm.reset();
  }

  private handleSubmissionError(error): void {
    this.errorDetected = true;
    this.event.publish("event:load-contest");
    const code = error?.error_code;
    switch (code) {
      case 'MAX_DAILYREGISTRATION_REACHED':
        const message_maxLimit = this.configService?.getConfig()?.MESSAGE_CONTEST_MAX_LIMITS ?? 'You have reached your maximum daily limit. Please try it after 24 hours.';
        this.displayMessage = message_maxLimit;
        break;
      case 'MAX_SAMEPLAYER_REACHED':
        const message_sameLimit = this.configService?.getConfig()?.MESSAGE_CONTEST_SAME_LIMITS ?? 'You have reached your maximum submission limit.';
        this.displayMessage = message_sameLimit;
        break;
      case 'CONTEST_NOT_ENABLED':
        const message_contestEnded = this.configService?.getConfig()?.MESSAGE_CONTEST_NOT_ENABLED ?? 'The contest has ended. Thank you.';
        this.displayMessage = message_contestEnded;
        break;
      default:
        const message_Default = this.configService?.getConfig()?.MESSAGE_CONTEST_DEFAULT_ERROR ?? 'The contest has ended. Thank you.';
        this.displayMessage = message_Default;
        break;
    }
  }

  get months() {

    let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    if (this.platform.is("capacitor")) {
      months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']
    }
    if (this.dateOfBirthYear.value == this.years[0]) {
      let extra = 11 - new Date().getMonth();
      for (extra; extra > 0; extra--) {
        months.pop();
        if (this.dateOfBirthMonth.value == months.length + 1) this.dateOfBirthMonth.setValue(months.length)
      }
    }
    return months;
  }

  get days() {
    let days = Array.apply(null, { length: 31 }).map(Number.call, Number)
    let displayDays = days.map(val => val + 1).map(val => (val < 10) ? "0" + val.toString() : val.toString());
    if (
      this.dateOfBirthYear.value == this.years[0] &&
      this.dateOfBirthMonth.value - 1 == this.months.indexOf(this.months[this.months.length - 1])
    ) {
      let extra = 31 - new Date().getDate();
      for (extra; extra > 0; extra--) {
        displayDays.pop();
        if (this.dateOfBirthDay.value == displayDays.length + 1) this.dateOfBirthDay.setValue(displayDays[displayDays.length - 1]);
      }
    }
    return displayDays;
  }

  get years() {
    let years = [];
    var min = new Date().getFullYear() - 100,
      max = new Date().getFullYear() - this.legalAge;
    for (var i = max; i >= min; i--) {
      years.push(i)
    }
    return years;
  }

  private onExitButton() {
    this.navigator.toLobby();
  }

}
